// src/components/CourseFilterIndicator.js

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import apiClient from '../api/client';

/**
 * Indicador global del filtro centralizado de cursos por categoría Moodle.
 *
 * Variants:
 *   - "chip"   (default): un Chip pequeño para AppBar / headers. Tooltip con detalles.
 *   - "banner": una Alert MUI dismissible al tope del contenido de una página.
 *
 * Si el endpoint no existe o falla, intenta caer a un valor por defecto
 * (`%/246/153%`) para no romper el render del componente. El componente
 * oculta el indicador si no hay paths configurados.
 */
const CourseFilterIndicator = ({ variant = 'chip' }) => {
  const [filterInfo, setFilterInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/admin/course-filter');
        if (!cancelled) {
          setFilterInfo(resp?.data ?? null);
        }
      } catch (e) {
        // Si el endpoint no está disponible (deploy viejo, error transitorio)
        // usamos un fallback razonable basado en el default histórico.
        if (!cancelled) {
          setFilterInfo({
            paths: ['%/246/153%'],
            source: 'fallback',
            raw: null,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !filterInfo || !Array.isArray(filterInfo.paths) || filterInfo.paths.length === 0) {
    return null;
  }

  const pathsLabel = filterInfo.paths.join(', ');
  const tooltipText = `Cursos filtrados por path de categoría Moodle: ${pathsLabel}`;

  if (variant === 'banner') {
    return (
      <Collapse in={!dismissed}>
        <Alert
          severity="info"
          icon={<FilterAltIcon />}
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              aria-label="Cerrar aviso de filtro"
              onClick={() => setDismissed(true)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <strong>Filtro activo:</strong> solo se muestran cursos bajo{' '}
          <code>{pathsLabel}</code> en Moodle.
        </Alert>
      </Collapse>
    );
  }

  // variant === 'chip' (default)
  return (
    <Tooltip title={tooltipText} arrow>
      <Chip
        icon={<FilterAltIcon />}
        label={`Filtro: ${pathsLabel}`}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ ml: 1, fontWeight: 600 }}
      />
    </Tooltip>
  );
};

export default CourseFilterIndicator;
